import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store/hooks";
import { useToast } from "../../contexts/ToastContext";
// import apiService from "../../services/api"; // Not used in this hook
import {
  fetchGroups,
  createGroup,
  updateGroup,
  deleteGroup,
} from "../../store/slices/bulkEmailSlice";

export const useGroups = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const {
    groups,
    loading: groupsLoading,
    error: groupsError,
  } = useSelector((state: any) => state.bulkEmail.groups);

  // Modal states
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any>(null);
  const [deletingGroup, setDeletingGroup] = useState<any>(null);

  // Group form state
  const [newGroup, setNewGroup] = useState({
    name: "",
    description: "",
    recipients: [] as any[],
  });

  // Recipients modal state
  const [selectedRecipients, setSelectedRecipients] = useState<any[]>([]);
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);
  const [availableRecipients, setAvailableRecipients] = useState<any[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>(
    []
  );
  const [recipientsSearchTerm, setRecipientsSearchTerm] = useState("");
  const [recipientsCurrentPage, setRecipientsCurrentPage] = useState(1);
  const [recipientsItemsPerPage, setRecipientsItemsPerPage] = useState(50);
  const [recipientsTotalCount, setRecipientsTotalCount] = useState(0);
  const [allRecipients, setAllRecipients] = useState<any[]>([]);
  const [selectedAllFromDB, setSelectedAllFromDB] = useState(false);
  const [hasLoadedAllRecipients, setHasLoadedAllRecipients] = useState(false);
  const [loadingMoreRecipients, setLoadingMoreRecipients] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Computed values
  const filteredGroups = Array.isArray(groups)
    ? groups.filter((group) => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
          group.name?.toLowerCase().includes(searchLower) ||
          group.description?.toLowerCase().includes(searchLower) ||
          group.recipients?.some(
            (r: any) =>
              r.name?.toLowerCase().includes(searchLower) ||
              r.email?.toLowerCase().includes(searchLower)
          )
        );
      })
    : [];

  const filteredAvailableRecipients = Array.isArray(availableRecipients)
    ? availableRecipients.filter((recipient) => {
        if (!recipientsSearchTerm) return true;
        const searchLower = recipientsSearchTerm.toLowerCase();
        return (
          recipient.name?.toLowerCase().includes(searchLower) ||
          recipient.email?.toLowerCase().includes(searchLower)
        );
      })
    : [];

  const recipientsTotalPages = Math.ceil(
    recipientsTotalCount / recipientsItemsPerPage
  );

  // Load groups on mount
  useEffect(() => {
    dispatch(fetchGroups());
  }, [dispatch]);

  // Group CRUD operations
  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) {
      showToast("Please enter a group name", "error");
      return;
    }

    try {
      const createResult = await dispatch(
        createGroup({
          name: newGroup.name.trim(),
          description: newGroup.description.trim(),
          recipients: selectedRecipients,
        })
      );

      if (createResult.type.endsWith("/fulfilled")) {
        showToast("Group created successfully", "success");
        const fetchResult = await dispatch(fetchGroups());
        if (fetchResult.type.endsWith("/fulfilled")) {
          setNewGroup({ name: "", description: "", recipients: [] });
          setSelectedRecipients([]);
          setShowCreateGroupModal(false);
        }
      } else {
        showToast("Failed to create group", "error");
      }
    } catch (error) {
      showToast("Error creating group", "error");
    }
  };

  const handleEditGroup = (group: any) => {
    setEditingGroup(group);
    setNewGroup({
      name: group.name || "",
      description: group.description || "",
      recipients: group.recipients || [],
    });
    setSelectedRecipients(group.recipients || []);
    setShowEditGroupModal(true);
  };

  const handleUpdateGroup = async () => {
    if (!newGroup.name.trim() || !editingGroup) {
      showToast("Please enter a group name", "error");
      return;
    }

    const updateData = {
      id: editingGroup.id || editingGroup._id,
      name: newGroup.name.trim(),
      description: newGroup.description.trim(),
      recipients: selectedRecipients,
    };

    try {
      const updateResult = await dispatch(updateGroup(updateData));

      if (updateResult.type.endsWith("/fulfilled")) {
        showToast("Group updated successfully", "success");
        await dispatch(fetchGroups());
        setShowEditGroupModal(false);
        setEditingGroup(null);
      } else {
        showToast("Failed to update group", "error");
      }
    } catch (error) {
      showToast("Error updating group", "error");
    }
  };

  const handleDeleteGroup = (group: any) => {
    setDeletingGroup(group);
    setShowDeleteModal(true);
  };

  const confirmDeleteGroup = async () => {
    if (!deletingGroup) return;

    const groupId = deletingGroup.id || deletingGroup._id;

    try {
      const deleteResult = await dispatch(deleteGroup(groupId));
      if (deleteResult.type.endsWith("/fulfilled")) {
        showToast("Group deleted successfully", "success");
        setShowDeleteModal(false);
        setDeletingGroup(null);
      } else {
        showToast("Failed to delete group", "error");
      }
    } catch (error) {
      showToast("Error deleting group", "error");
    }
  };

  const handleRemoveRecipientFromGroup = (recipientId: string) => {
    const updatedRecipients = selectedRecipients.filter(
      (r) => r.id !== recipientId
    );
    setSelectedRecipients(updatedRecipients);
    setNewGroup((prev) => ({ ...prev, recipients: updatedRecipients }));
  };

  return {
    // State
    groups: filteredGroups,
    groupsLoading,
    groupsError,
    searchTerm,
    setSearchTerm,

    // Modal states
    showCreateGroupModal,
    setShowCreateGroupModal,
    showEditGroupModal,
    setShowEditGroupModal,
    showDeleteModal,
    setShowDeleteModal,
    editingGroup,
    deletingGroup,

    // Group form
    newGroup,
    setNewGroup,

    // Recipients modal
    selectedRecipients,
    setSelectedRecipients,
    showRecipientsModal,
    setShowRecipientsModal,
    availableRecipients,
    setAvailableRecipients,
    recipientsLoading,
    setRecipientsLoading,
    selectedRecipientIds,
    setSelectedRecipientIds,
    recipientsSearchTerm,
    setRecipientsSearchTerm,
    recipientsCurrentPage,
    setRecipientsCurrentPage,
    recipientsItemsPerPage,
    setRecipientsItemsPerPage,
    recipientsTotalCount,
    setRecipientsTotalCount,
    allRecipients,
    setAllRecipients,
    selectedAllFromDB,
    setSelectedAllFromDB,
    hasLoadedAllRecipients,
    setHasLoadedAllRecipients,
    loadingMoreRecipients,
    setLoadingMoreRecipients,

    // Computed values
    filteredAvailableRecipients,
    recipientsTotalPages,

    // Actions
    handleCreateGroup,
    handleEditGroup,
    handleUpdateGroup,
    handleDeleteGroup,
    confirmDeleteGroup,
    handleRemoveRecipientFromGroup,

    // State setters for modal management
    setEditingGroup,
    setDeletingGroup,
  };
};
